import * as React from 'react';
import { observer } from 'mobx-react';
import { observable, action, computed } from 'mobx';
import { DatePicker, Dropdown, IDropdownOption } from 'office-ui-fabric-react';
import { NotificationHandler } from '@app/Components/ToastNotification';

import { securityService } from '@app/Services/SecurityService';
import ApiService from '@app/Services/ApiService';
import DateTimeService from '@app/Services/DateTimeService';
import { ApiUrls, PersonFleetOverrideType, Permissions } from '@app/AppConstants';
import { EnumHelpers } from '@app/Services/EnumHelpers';
import { DateTimePickerService } from '@app/Services/DateTimePickerService';
import { IModalDialogContent, ModalWindow, ModalDialogOptions, ModalButtonType } from '@app/Components/Modal';
import { AzureAuth } from '@app/Components/AzureAuth';

type NewPersonFleetOverrideDialogProps = {
    personId: string;
};

@observer
export class NewPersonFleetOverrideDialog extends React.Component<NewPersonFleetOverrideDialogProps, {}> implements IModalDialogContent<void> {
    private _store: NewPersonFleetOverrideStore;

    constructor(props: NewPersonFleetOverrideDialogProps) {
        super(props);
        this._store = new NewPersonFleetOverrideStore(props.personId);
    }

    public getModalOptions(window: ModalWindow<void>): ModalDialogOptions<void> {
        const { hasPermission } = securityService;
        return {
            title: 'New Person Fleet Override',
            buttons: [{
                type: ModalButtonType.Cancel,
                onClick: () => {
                    window.close();
                }
            }, {
                type: ModalButtonType.Save,
                isDisabled: !this._store.isValidForm || !hasPermission(Permissions.EditObjectPersonFleetOverride, !AzureAuth.isReadOnly),
                onClick: async () => {
                    if (await this._store.onSave()){
                        window.close(ModalButtonType.Save);
                    }
                }
            }],
        };
    }

    render() {
        return (
            <>
                <DatePicker
                    label="Date From"
                    className={this.dateClassName}
                    allowTextInput={true}
                    formatDate={DateTimePickerService.onFormatDate}
                    parseDateFromString={DateTimePickerService.onParseDateFromString}
                    onSelectDate={this._store.onSelectFromDate}
                    isRequired={true}
                    value={this._store.dateFrom || undefined} />
                {this._store.isInvalidRange && <span className="ms-TextField-errorMessage errorMessage">Invalid date range</span>}
                <DatePicker
                    label="Date To"
                    className={this.dateClassName}
                    allowTextInput={true}
                    formatDate={DateTimePickerService.onFormatDate}
                    parseDateFromString={DateTimePickerService.onParseDateFromString}
                    onSelectDate={this._store.onSelectToDate}
                    value={this._store.dateTo || undefined} />
                {this._store.isInvalidRange && <span className="ms-TextField-errorMessage errorMessage">Invalid date range</span>}
                <Dropdown
                    label="Fleet Type"
                    options={this._store.fleetTypeOptions}
                    onChange={this._store.onChange}
                    required={true}
                    selectedKey={this._store.fleetType || ''}
                />
                {this._store.validationMessage && <p className="ms-TextField-errorMessage errorMessage">{this._store.validationMessage}</p>}
            </>
        );
    }

    @computed
    get dateClassName() {
        return 'date-field' + (this._store.isInvalidRange ? ' invalid' : '');
    }
}

class NewPersonFleetOverrideStore {
    @observable dateFrom: Date | null;
    @observable dateTo: Date | null;
    @observable fleetType: string;
    @observable isInvalidRange: boolean = false;
    @observable validationMessage: string | null = '';
    private personId: string;

    constructor(personId: string) {
        this.personId = personId;
    }

    @action
    onSelectFromDate = (date: Date | null | undefined): void => {
        this.dateFrom = date ? DateTimeService.date(date.getFullYear(), date.getMonth(), date.getDate()) : null;
        this.validateDate();
    };

    @action
    onSelectToDate = (date: Date | null | undefined): void => {
        this.dateTo = date ? DateTimeService.date(date.getFullYear(), date.getMonth(), date.getDate()) : null;
        this.validateDate();
    };

    @action
    validateDate = () => {
        if (this.dateFrom && this.dateTo) this.isInvalidRange = DateTimeService.isBeforeDate(this.dateTo, this.dateFrom);
        if (!this.dateTo) this.isInvalidRange = false;
    };

    @action
    onChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        if (option) {
            this.fleetType = '' + option.key; // option.key is string|number type
        }
    };

    @computed
    get fleetTypeOptions() {
        return EnumHelpers.keys(PersonFleetOverrideType).map((key) => {
            return { key, text: PersonFleetOverrideType[key as keyof PersonFleetOverrideType] };
        });
    }

    @computed
    get isValidForm() {
        return this.dateFrom && !this.isInvalidRange && this.fleetType;
    }

    @action.bound
    async onSave() {
        if (!this.personId) return;
        const params = {
            personId: this.personId,
            dateFrom: this.dateFrom,
            dateTo: this.dateTo,
            type: this.fleetType
        };

        const { data } = await ApiService.postData(ApiUrls.PersonFleetOverrideUrl, params);
        if (!data.hasErrors) {
            this.validationMessage = null;
            NotificationHandler.showSuccess('New Person Fleet Override was added', 'Success message');
            return true;
        } else {
            this.validationMessage = data.errors.join(' ');
        }

        return false;
    }
}
